import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styles from './Contact.module.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import { Helmet } from 'react-helmet';

const Contact = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [status, setStatus] = useState('idle'); // idle | loading | success | error

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');

    try {
      // Point this to where your PHP script is hosted
      const response = await fetch('https://yeskondomedia.com/contact.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (result.success) {
        setStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setStatus('error');
    }
  };

  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Yeskondo Media Pvt Ltd",
    "url": "https://www.yeskondomedia.com/contact",
    "logo": "https://www.yeskondomedia.com/logo.png",
    "image": "https://www.yeskondomedia.com/og-image.jpg",
    "description": "Contact Yeskondo Media, the best marketing company in Pune, to discuss your digital transformation, web development, and SEO requirements.",
    "telephone": "+91-9922966739",
    "email": "info@yeskondomedia.com",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Deccan",
      "addressLocality": "Pune",
      "addressRegion": "Maharashtra",
      "postalCode": "411004",
      "addressCountry": "IN"
    },
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday"
      ],
      "opens": "09:30",
      "closes": "18:30"
    }
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://www.yeskondomedia.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Contact Us",
        "item": "https://www.yeskondomedia.com/contact"
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Contact Us | Best Marketing Company in Pune | Yeskondo Media</title>
        <meta name="description" content="Get in touch with Yeskondo Media, the best marketing company in Pune. Contact us today for premium digital marketing, web development, and SEO services." />
        <meta name="keywords" content="contact Yeskondo Media, best marketing company in pune contact, digital marketing agency pune phone number, SEO services pune contact" />
        <link rel="canonical" href="https://www.yeskondomedia.com/contact" />

        {/* Open Graph Tags */}
        <meta property="og:title" content="Contact Us | Best Marketing Company in Pune | Yeskondo Media" />
        <meta property="og:description" content="Reach out to Yeskondo Media in Pune to start your brand's digital transformation. Our experts are ready to help." />
        <meta property="og:image" content="https://www.yeskondomedia.com/og-image.jpg" />
        <meta property="og:url" content="https://www.yeskondomedia.com/contact" />
        <meta property="og:type" content="website" />

        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact Us | Best Marketing Company in Pune" />
        <meta name="twitter:description" content="Get in touch with Yeskondo Media for expert digital marketing, web development, and SEO solutions." />
        <meta name="twitter:image" content="https://www.yeskondomedia.com/og-image.jpg" />

        <script type="application/ld+json">{JSON.stringify(schemaMarkup)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <section className={styles.contact_page}>

        {/* ══ 1. DARK HERO BANNER ══ */}
        <div className={styles.page_hero}>
          <div className={styles.hero_inner}>
            {/* Breadcrumbs */}
            <nav className={styles.breadcrumb} aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <span className={styles.breadcrumb_sep}>›</span>
              <span className={styles.breadcrumb_current}>Contact Us</span>
            </nav>

            <span className={styles.eyebrow}>Let's Connect</span>
            <h1 className={styles.main_title}>
              Start Your Digital <br />
              <span className={styles.highlight}>Transformation Today</span>
            </h1>
            <p className={styles.subtitle}>
              Partner with the best marketing company in Pune. Fill out the form below or reach us directly to discuss your brand's future.
            </p>
          </div>
        </div>

        {/* ══ 2. CONTACT DETAILS GRID ══ */}
        <div className={styles.contact_header}>
          <div className={styles.contact_header_icons}>
            <div className={styles.icon_card}>
              <div className={styles.icon_holder}>
                <i className="fa-solid fa-location-dot"></i>
              </div>
              <h3>Visit Our Office</h3>
              <p>Deccan Gymkhana,<br />Pune, Maharashtra 411004</p>
            </div>

            <div className={styles.icon_card}>
              <div className={styles.icon_holder}>
                <i className="fa-solid fa-phone"></i>
              </div>
              <h3>Call Us Directly</h3>
              <p>Mon - Sat, 9:30 AM to 6:30 PM<br />
                <a href="tel:+919922966739">+91 99229 66739</a></p>
            </div>

            <div className={styles.icon_card}>
              <div className={styles.icon_holder}>
                <i className="fa-solid fa-envelope"></i>
              </div>
              <h3>Send an Email</h3>
              <p>We typically reply within 2 hours.<br />
                <a href="mailto:info@yeskondomedia.com">info@yeskondomedia.com</a></p>
            </div>
          </div>
        </div>

        {/* ══ 3. CONTACT FORM AREA ══ */}
        <div className={styles.contact_form_main}>
          <div className={styles.contactform_flexbox}>

            <div className={styles.contact_form}>
              <h2>Send Us a Message</h2>
              <p className={styles.form_desc}>Have a project in mind? Let's build something great together.</p>

              {status === 'success' && (
                <div className={styles.alert_success}>
                  <i className="fa-solid fa-check-circle"></i>
                  Message sent successfully! Our team will contact you shortly.
                </div>
              )}
              {status === 'error' && (
                <div className={styles.alert_error}>
                  <i className="fa-solid fa-triangle-exclamation"></i>
                  Something went wrong. Please try again or call us directly.
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className={styles.flexbox}>
                  <div className={styles.form_group}>
                    <label htmlFor="name">Full Name *</label>
                    <input type="text" id="name" name="name" placeholder="John Doe" required value={formData.name} onChange={handleChange} disabled={status === 'loading'} />
                  </div>
                  <div className={styles.form_group}>
                    <label htmlFor="email">Email Address *</label>
                    <input type="email" id="email" name="email" placeholder="john@company.com" required value={formData.email} onChange={handleChange} disabled={status === 'loading'} />
                  </div>
                </div>

                <div className={styles.form_group}>
                  <label htmlFor="subject">Subject *</label>
                  <input type="text" id="subject" name="subject" placeholder="How can we help you?" required value={formData.subject} onChange={handleChange} disabled={status === 'loading'} />
                </div>

                <div className={styles.form_group}>
                  <label htmlFor="message">Message *</label>
                  <textarea id="message" name="message" placeholder="Tell us about your project, goals, and budget..." required value={formData.message} onChange={handleChange} disabled={status === 'loading'}></textarea>
                </div>

                <button type="submit" className={styles.submit_button} disabled={status === 'loading'}>
                  {status === 'loading' ? (
                    <span className={styles.loader}>Sending...</span>
                  ) : (
                    <>Send Message <i className="fa-solid fa-paper-plane" style={{ marginLeft: '8px' }}></i></>
                  )}
                </button>
              </form>
            </div>

            <div className={styles.contact_form_image}>
              {/* Image background handled in CSS */}
              <div className={styles.image_overlay}>
                <h3>Ready to grow?</h3>
                <p>Join over 150+ happy clients who trust Yeskondo Media with their brand's digital journey.</p>
              </div>
            </div>

          </div>
        </div>

      </section>
    </>
  );
};

export default Contact;
